import "./Modals.scss";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  CheckCircleTwoTone,
  CheckOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { LeaveRequest, ModalProps, User, UserSignature } from "../../utils/types";
import SearchInput from "../Search/SearchInput";
import {
  fetchData,
  getErrorMessage,
  hasRoleAccess,
  Notify,
  postData,
} from "../../utils/constants";
import { apiClient } from "../../utils/httpClient";
import { useUser } from "../../context/UserContext";
import { signaturePDF, SignLeaveBtn } from "../Buttons/SignLeaveBtn";
import { addWorkingDaysInclusive, toUtcDateString } from "../../utils/date";

const leaveTypes: { key: string; text: string; value: string; maleOnly?: boolean }[] = [
  {
    key: "ANNUAL_LEAVE",
    text: "Annual leave",
    value: "ANNUAL_LEAVE",
  },
  {
    key: "UNPAID_LEAVE",
    text: "Unpaid Leave",
    value: "UNPAID_LEAVE",
  },
  {
    key: "BLOOD_DONATION",
    text: "Blood Donation",
    value: "BLOOD_DONATION",
  },
  {
    key: "DEATH_OF_RELATIVE",
    text: "Death of Relative",
    value: "DEATH_OF_RELATIVE",
  },
  {
    key: "CHILD_BIRTH",
    text: "Child Birth",
    value: "CHILD_BIRTH",
    maleOnly: true,
  },
  {
    key: "MARRIAGE",
    text: "Marriage",
    value: "MARRIAGE",
  },
];

// The primary "Leave type" selector only ever shows two choices - Annual
// leave, or a single "Other leave request" entry that reveals a secondary
// selector for the 5 specific types below, instead of listing all 6 flat.
const OTHER_LEAVE_TYPE_VALUE = "OTHER";
const otherLeaveTypes = leaveTypes.filter(
  (item) => item.value !== "ANNUAL_LEAVE",
);
const DEFAULT_OTHER_LEAVE_TYPE = otherLeaveTypes[0].value;

const UNPAID_LEAVE_REASONS = [
  { label: "Professional training/Studies", value: "PROFESSIONAL_TRAINING" },
  { label: "Personal needs", value: "PERSONAL_STUDIES" },
];

const SPECIAL_CASE_INFO_TEXT =
  "Se va selecta caz special pentru situatii exceptionale cand inmormantarea este la o data ulterioara care nu poate sa fie estimata la data cererii";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

type UserModalProps = ModalProps & {
  selectedStartDate?: Date;
  // selectedUser: User | null;
  selectedEndDate?: Date;
  refreshData?: () => void;
  withButton?: boolean;
  // When set, the modal edits and resubmits this existing request (returned
  // for correction) instead of creating a new one - see NewLeaveTypeCreationService.resubmitLeaveRequest.
  editRequest?: LeaveRequest | null;
};

const LeaveReqModal = ({
  isOpen,
  setIsOpen,
  selectedStartDate,
  selectedEndDate,
  withButton = true,
  // selectedUser,
  refreshData,
  editRequest,
}: UserModalProps) => {
  const isEditMode = !!editRequest;
  const { roles, authUser } = useUser();
  const [user, setUser] = useState<User | null>(null);
  const [startDate, setStartDate] = useState<Date>(
    selectedStartDate ? selectedStartDate : new Date(),
  );
  const [endDate, setEndDate] = useState<Date>(
    selectedEndDate ? selectedEndDate : startDate,
  );
  const [leaveType, setLeaveType] = useState(leaveTypes[0].value);
  const [unpaidLeaveReason, setUnpaidLeaveReason] = useState<
    string | undefined
  >(undefined);
  const [eventDate, setEventDate] = useState<Date | undefined>(undefined);
  const [specialCase, setSpecialCase] = useState<boolean>(false);
  const [puericulturaRequested, setPuericulturaRequested] =
    useState<boolean>(false);
  const [documentFileList, setDocumentFileList] = useState<UploadFile[]>([]);
  const [puericulturaFileList, setPuericulturaFileList] = useState<
    UploadFile[]
  >([]);
  const [comment, setComment] = useState<string>("");
  const [userSearchValue, setUserSearchValue] = useState<string>("");
  const [substitute, setSubstitute] = useState<User | null>(null);
  const [substituteSearchValue, setSubstituteSearchValue] =
    useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const [pdf, setPDF] = useState<string>("");
  const [mySignature, setMySignature] = useState<UserSignature | null>(null);
  const useAutoSignature = !!(mySignature?.useSignatureAuto && mySignature?.signatureImage);
  const [legalHolidayDates, setLegalHolidayDates] = useState<Set<string>>(
    new Set(),
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/users/getAllUsers`,
        setUsers,
      );
    } catch (error) {
      console.error("Error while fetching data:", error);
      Notify("error", "Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLegalHolidays = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/holidays/getAllHolidays`,
        (data: { date: string }[]) =>
          setLegalHolidayDates(new Set(data.map((h) => h.date.slice(0, 10)))),
      );
    } catch (error) {
      console.error("Error while fetching legal holidays:", error);
    }
  }, []);

  const isLegalHoliday = useCallback(
    (date: Date) => legalHolidayDates.has(toUtcDateString(date)),
    [legalHolidayDates],
  );

  // Child Birth's End date is derived, not picked: Start date + 10 working
  // days, or +15 if Puericultura is requested (matches the backend's
  // BASE_MAX_WORKING_DAYS / PUERICULTURA_EXTRA_WORKING_DAYS in
  // ChildBirthLeaveRequestHandler).
  const computeChildBirthEndDate = useCallback(
    (start: Date, puericultura: boolean) =>
      addWorkingDaysInclusive(start, puericultura ? 15 : 10, isLegalHoliday),
    [isLegalHoliday],
  );

  const isAnnualLeave = leaveType === "ANNUAL_LEAVE";
  const isMarriageAfterWedding =
    leaveType === "MARRIAGE" &&
    !!eventDate &&
    eventDate.getTime() < new Date().setHours(0, 0, 0, 0);

  const START_END_DATE_EXTRA: Record<string, string> = {
    ANNUAL_LEAVE: "The first and last day of your annual leave.",
    UNPAID_LEAVE: "Start date must be at least 1 calendar month after today.",
    CHILD_BIRTH:
      "End date is calculated automatically as Start date + 10 working days (+15 if Puericultura is checked), and can be at most 56 calendar days after the child's birth date.",
    DEATH_OF_RELATIVE:
      "Maximum 2 working days, and start date must be within 5 days of the date of death.",
    MARRIAGE: "Maximum 5 working days.",
    BLOOD_DONATION: "The day you donate blood - maximum 1 day.",
  };

  const COMMENT_EXTRA: Record<string, string> = {
    UNPAID_LEAVE: "Required - please explain the reason for this request.",
    MARRIAGE:
      "Required if the request is made before the marriage date, or if the start date is more than 30 days after the marriage date.",
  };

  // Mirrors the exact conditions the submit button used to be silently
  // disabled on - now surfaced per-field as a red border + reason message
  // instead of just an unexplained disabled button.
  type FieldErrorKey =
    | "signature"
    | "unpaidLeaveReason"
    | "comment"
    | "eventDate"
    | "documentFile"
    | "puericulturaFile";

  const fieldErrors: Partial<Record<FieldErrorKey, string>> = {};

  const signatureRequired = isAnnualLeave ? !pdf : !isEditMode && !pdf;
  if (signatureRequired) {
    fieldErrors.signature = "Signature is required to submit the request.";
  }

  if (!isAnnualLeave) {
    if (leaveType === "UNPAID_LEAVE") {
      if (!unpaidLeaveReason) {
        fieldErrors.unpaidLeaveReason =
          "Please select a reason for the unpaid leave.";
      }
      if (!comment) {
        fieldErrors.comment = "Please explain the reason for this request.";
      }
    }

    if (leaveType === "BLOOD_DONATION" && !eventDate) {
      fieldErrors.eventDate = "Please select the blood donation date.";
    }

    if (leaveType === "DEATH_OF_RELATIVE") {
      if (!eventDate) {
        fieldErrors.eventDate = "Please select the date of death.";
      }
      if (!isEditMode && documentFileList.length === 0) {
        fieldErrors.documentFile = "Please upload the death certificate.";
      }
    }

    if (leaveType === "CHILD_BIRTH") {
      if (!eventDate) {
        fieldErrors.eventDate = "Please select the child's birth date.";
      }
      if (!isEditMode && documentFileList.length === 0) {
        fieldErrors.documentFile = "Please upload the birth certificate.";
      }
      if (
        puericulturaRequested &&
        !isEditMode &&
        puericulturaFileList.length === 0
      ) {
        fieldErrors.puericulturaFile =
          "Please upload the puericultura certificate.";
      }
    }

    if (leaveType === "MARRIAGE") {
      if (!eventDate) {
        fieldErrors.eventDate = "Please select the marriage date.";
      }
      if (
        isMarriageAfterWedding &&
        !isEditMode &&
        documentFileList.length === 0
      ) {
        fieldErrors.documentFile = "Please upload the marriage certificate.";
      }
    }
  }

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const errorProps = (key: FieldErrorKey) =>
    showErrors && fieldErrors[key]
      ? { validateStatus: "error" as const, help: fieldErrors[key] }
      : {};

  const errorBoxStyle = (key: FieldErrorKey): CSSProperties | undefined =>
    showErrors && fieldErrors[key]
      ? {
          border: "1px solid #ff4d4f",
          borderRadius: 6,
          padding: 8,
          display: "inline-block",
        }
      : undefined;

  const resetForm = () => {
    setUser(null);
    setUserSearchValue("");
    setStartDate(new Date());
    setEndDate(new Date());
    setLeaveType(leaveTypes[0].value);
    setUnpaidLeaveReason(undefined);
    setEventDate(undefined);
    setSpecialCase(false);
    setPuericulturaRequested(false);
    setDocumentFileList([]);
    setPuericulturaFileList([]);
    setComment("");
    setSubstitute(null);
    setSubstituteSearchValue("");
    setPDF("");
    setMySignature(null);
    setShowErrors(false);
  };

  const handleCreateLeaveRequest = useCallback(async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (isAnnualLeave) {
        const formData = {
          startDate: toUtcDateString(startDate),
          endDate: toUtcDateString(endDate),
          substituteId: substitute?.id,
          comment,
          leavePDF: pdf,
        };
        await postData(
          `${process.env.REACT_APP_API_URL}/leave-requests`,
          formData,
        );
      } else {
        const documentFile = documentFileList[0]?.originFileObj as
          | File
          | undefined;
        const documentBase64 = documentFile
          ? await fileToBase64(documentFile)
          : undefined;

        const formData = {
          requestType: leaveType,
          startDate: toUtcDateString(startDate),
          endDate: toUtcDateString(endDate),
          substituteId: substitute?.id,
          comment,
          unpaidLeaveReason,
          eventDate: eventDate ? toUtcDateString(eventDate) : undefined,
          specialCase: leaveType === "DEATH_OF_RELATIVE" ? specialCase : undefined,
          puericulturaRequested:
            leaveType === "CHILD_BIRTH" ? puericulturaRequested : undefined,
          documentBase64,
          documentFileName: documentFile?.name,
          documentMimeType: documentFile?.type,
          leavePDF: pdf,
        };
        const response = isEditMode
          ? await apiClient.put(
              `${process.env.REACT_APP_API_URL}/leave-requests/typed/${editRequest?.id}`,
              formData,
            )
          : await apiClient.post(
              `${process.env.REACT_APP_API_URL}/leave-requests/typed`,
              formData,
            );

        const puericulturaFile = puericulturaFileList[0]?.originFileObj as
          | File
          | undefined;
        if (
          leaveType === "CHILD_BIRTH" &&
          puericulturaRequested &&
          puericulturaFile &&
          response.data?.id
        ) {
          const puericulturaBase64 = await fileToBase64(puericulturaFile);
          await apiClient.post(
            `${process.env.REACT_APP_API_URL}/leave-requests/${response.data.id}/documents`,
            {
              documentType: "PUERICULTURA_CERTIFICATE",
              fileBase64: puericulturaBase64,
              fileName: puericulturaFile.name,
              mimeType: puericulturaFile.type,
            },
          );
        }
      }
      Notify(
        "success",
        isEditMode
          ? "Request resubmitted successfully!"
          : "Request created successfully!",
      );
      resetForm();
      if (refreshData) refreshData();
      setIsOpen(false);
    } catch (error) {
      console.error("POST error:", error);
      Notify(
        "error",
        getErrorMessage(
          error,
          `Oops! Something went wrong while ${isEditMode ? "resubmitting" : "creating"} the request.`,
        ),
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    comment,
    documentFileList,
    editRequest?.id,
    endDate,
    eventDate,
    isAnnualLeave,
    isEditMode,
    leaveType,
    pdf,
    puericulturaFileList,
    puericulturaRequested,
    refreshData,
    setIsOpen,
    specialCase,
    startDate,
    substitute?.id,
    unpaidLeaveReason,
  ]);

  const attemptSubmit = () => {
    if (hasFieldErrors) {
      setShowErrors(true);
      Notify(
        "error",
        "Please fix the highlighted fields before submitting.",
      );
      return;
    }
    handleCreateLeaveRequest();
  };

  useEffect(() => {
    if (
      endDate < startDate ||
      endDate.getFullYear() !== startDate.getFullYear()
    ) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (isOpen) {
      // setUser(selectedUser);
      loadData();
      loadLegalHolidays();
      signaturePDF.subscribe({
        next: (val) => {
          setPDF(val);
        },
      });
    }
  }, [isOpen, loadData, loadLegalHolidays]);

  useEffect(() => {
    if (!isOpen) return;

    apiClient
      .get<UserSignature>(`${process.env.REACT_APP_API_URL}/users/me/signature`)
      .then((response) => {
        setMySignature(response.data);
        if (response.data.useSignatureAuto && response.data.signatureImage) {
          setPDF(response.data.signatureImage);
        }
      })
      .catch((error) => {
        console.error("Failed to load saved signature:", error);
      });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const newStart = selectedStartDate || new Date();
    const newEnd = selectedEndDate || newStart;

    setStartDate((prev) =>
      prev.getTime() !== newStart.getTime() ? newStart : prev,
    );
    setEndDate((prev) => (prev.getTime() !== newEnd.getTime() ? newEnd : prev));
  }, [isOpen, selectedStartDate, selectedEndDate]);

  // Pre-fill from the request being resubmitted - runs after the defaulting
  // effect above so it wins for start/end date too.
  useEffect(() => {
    if (!isOpen || !editRequest) return;

    setLeaveType(editRequest.requestType);
    setStartDate(
      editRequest.startDate ? new Date(editRequest.startDate) : new Date(),
    );
    setEndDate(
      editRequest.endDate ? new Date(editRequest.endDate) : new Date(),
    );
    setComment(editRequest.comment || "");
    setEventDate(
      editRequest.eventDate ? new Date(editRequest.eventDate) : undefined,
    );
    setSpecialCase(!!editRequest.specialCase);
    setPuericulturaRequested(!!editRequest.puericulturaRequested);
    setUnpaidLeaveReason(editRequest.unpaidLeaveReason);
  }, [isOpen, editRequest]);

  useEffect(() => {
    if (authUser && users.length && !user) {
      setLoading(true);
      const self = users.find((u) => u.email === authUser.email);
      if (self) {
        setUser(self);
      }
      setLoading(false);
    }
  }, [authUser, setUser, user, users]);

  return (
    <>
      {withButton && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsOpen(true)}
          className="!bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 hover:!border-emerald-700"
        >
          Add new leave
        </Button>
      )}
      <Modal
        title={isEditMode ? "Edit & Resubmit Request" : "Add new leave request"}
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        width={760}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsOpen(false)}
            className="!bg-slate-900 !text-white hover:!bg-slate-800"
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={attemptSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
            icon={<CheckOutlined />}
            className="!bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 hover:!border-emerald-700"
          >
            {isEditMode ? "Resubmit Request" : "Create Request"}
          </Button>,
        ]}
      >
        {loading && user ? (
          <div className="py-10 text-center">
            <Spin />
            <Typography.Paragraph className="mt-3">
              Loading data...
            </Typography.Paragraph>
          </div>
        ) : (
          <Form layout="vertical" className="mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hasRoleAccess(roles, ["ADMIN", "PONTATOR"]) && (
                <Form.Item label="User">
                  {user ? (
                    <>
                    <Input
                      value={`${user.firstName} ${user.lastName}`}
                      readOnly
                      
                      // addonAfter={
                      //   <Button
                      //     type="text"
                      //     icon={<CloseOutlined />}
                      //     onClick={() => {
                      //       // setUser(null);
                      //       setUserSearchValue("");
                      //     }}
                      //   />
                      // }
                    />
                    this is rendered ffs
                    </>

                  ) : (
                    <SearchInput
                      onSelect={() => {
                        setUserSearchValue("");
                      }}
                      options={users}
                      toDisplay={["firstName", "lastName"]}
                      value={userSearchValue}
                      onChangeValue={setUserSearchValue}
                    />
                  )}
                </Form.Item>
              )}

              <Form.Item
                label="Leave type"
                extra="Choose 'Annual leave', or 'Other leave request' to pick one of the 5 special leave types below."
              >
                <Select
                  value={isAnnualLeave ? "ANNUAL_LEAVE" : OTHER_LEAVE_TYPE_VALUE}
                  disabled={isEditMode}
                  options={[
                    { label: "Annual leave", value: "ANNUAL_LEAVE" },
                    { label: "Other leave request", value: OTHER_LEAVE_TYPE_VALUE },
                  ]}
                  onChange={(value) =>
                    setLeaveType(
                      value === "ANNUAL_LEAVE"
                        ? "ANNUAL_LEAVE"
                        : DEFAULT_OTHER_LEAVE_TYPE,
                    )
                  }
                  className="w-full"
                />
              </Form.Item>

              {!isAnnualLeave && (
                <Form.Item
                  label="Request type"
                  extra="The specific type of leave you're requesting."
                >
                  <Select
                    value={leaveType}
                    disabled={isEditMode}
                    options={otherLeaveTypes
                      .filter(
                        (item) =>
                          !item.maleOnly ||
                          user?.gender?.toLowerCase() === "male",
                      )
                      .map((item) => ({
                        label: item.text,
                        value: item.value,
                      }))}
                    onChange={(value) => {
                      setLeaveType(value);
                      if (value === "CHILD_BIRTH") {
                        setEndDate(computeChildBirthEndDate(startDate, false));
                      }
                    }}
                    className="w-full"
                  />
                </Form.Item>
              )}

              {leaveType === "UNPAID_LEAVE" && (
                <Form.Item
                  label="Reason"
                  required
                  extra="Select whether this leave is for professional training/studies or for personal needs."
                  {...errorProps("unpaidLeaveReason")}
                >
                  <Select
                    value={unpaidLeaveReason}
                    placeholder="Select a reason"
                    options={UNPAID_LEAVE_REASONS}
                    onChange={(value) => setUnpaidLeaveReason(value)}
                    className="w-full"
                  />
                </Form.Item>
              )}

              {leaveType === "BLOOD_DONATION" && (
                <Form.Item
                  label="Blood donation date"
                  required
                  extra="Must be a future date - the request must be submitted before you donate."
                  {...errorProps("eventDate")}
                >
                  <DatePicker
                    value={eventDate ? dayjs(eventDate) : null}
                    onChange={(date) => setEventDate(date ? date.toDate() : undefined)}
                    format="YYYY-MM-DD"
                    disabledDate={(current) =>
                      !!current && !current.isAfter(dayjs(), "day")
                    }
                    className="w-full"
                  />
                </Form.Item>
              )}

              {leaveType === "DEATH_OF_RELATIVE" && (
                <Form.Item
                  label="Date of death"
                  required
                  extra="The request must be submitted within 5 days of this date."
                  {...errorProps("eventDate")}
                >
                  <DatePicker
                    value={eventDate ? dayjs(eventDate) : null}
                    onChange={(date) => setEventDate(date ? date.toDate() : undefined)}
                    format="YYYY-MM-DD"
                    disabledDate={(current) => !!current && current.isAfter(dayjs(), "day")}
                    className="w-full"
                  />
                </Form.Item>
              )}

              {leaveType === "DEATH_OF_RELATIVE" && (
                <Form.Item
                  label={
                    <span>
                      Special case{" "}
                      <Tooltip title={SPECIAL_CASE_INFO_TEXT}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  extra="Check this only if the funeral date isn't known yet - the request will be kept as a record with no start/end date."
                >
                  <Checkbox
                    checked={specialCase}
                    onChange={(e) => setSpecialCase(e.target.checked)}
                  >
                    Funeral date not yet known
                  </Checkbox>
                </Form.Item>
              )}

              {leaveType === "DEATH_OF_RELATIVE" && (
                <Form.Item
                  label="Death certificate"
                  required={!isEditMode}
                  extra="Required. Accepted formats: PDF, JPG, JPEG."
                  {...errorProps("documentFile")}
                >
                  <div style={errorBoxStyle("documentFile")}>
                    <Upload
                      beforeUpload={() => false}
                      fileList={documentFileList}
                      onChange={({ fileList: newFileList }) =>
                        setDocumentFileList(newFileList.slice(-1))
                      }
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Select file</Button>
                    </Upload>
                  </div>
                </Form.Item>
              )}

              {leaveType === "CHILD_BIRTH" && (
                <Form.Item
                  label="Child's birth date"
                  required
                  extra="The request must be submitted at least 2 working days before the start date."
                  {...errorProps("eventDate")}
                >
                  <DatePicker
                    value={eventDate ? dayjs(eventDate) : null}
                    onChange={(date) => setEventDate(date ? date.toDate() : undefined)}
                    format="YYYY-MM-DD"
                    disabledDate={(current) => !!current && current.isAfter(dayjs(), "day")}
                    className="w-full"
                  />
                </Form.Item>
              )}

              {leaveType === "CHILD_BIRTH" && (
                <Form.Item
                  label="Puericultura"
                  extra="Optional. Adds 5 extra working days on top of the base 10, contiguous with the leave period."
                >
                  <Checkbox
                    checked={puericulturaRequested}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setPuericulturaRequested(checked);
                      setEndDate(computeChildBirthEndDate(startDate, checked));
                    }}
                  >
                    Add 5 working days for Puericultura
                  </Checkbox>
                </Form.Item>
              )}

              {leaveType === "CHILD_BIRTH" && (
                <Form.Item
                  label="Birth certificate"
                  required={!isEditMode}
                  extra="Required for the base 10 working days."
                  {...errorProps("documentFile")}
                >
                  <div style={errorBoxStyle("documentFile")}>
                    <Upload
                      beforeUpload={() => false}
                      fileList={documentFileList}
                      onChange={({ fileList: newFileList }) =>
                        setDocumentFileList(newFileList.slice(-1))
                      }
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Select file</Button>
                    </Upload>
                  </div>
                </Form.Item>
              )}

              {leaveType === "CHILD_BIRTH" && puericulturaRequested && (
                <Form.Item
                  label="Puericultura certificate"
                  required={!isEditMode}
                  extra="Required only because Puericultura is selected above."
                  {...errorProps("puericulturaFile")}
                >
                  <div style={errorBoxStyle("puericulturaFile")}>
                    <Upload
                      beforeUpload={() => false}
                      fileList={puericulturaFileList}
                      onChange={({ fileList: newFileList }) =>
                        setPuericulturaFileList(newFileList.slice(-1))
                      }
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Select file</Button>
                    </Upload>
                  </div>
                </Form.Item>
              )}

              {leaveType === "MARRIAGE" && (
                <Form.Item
                  label="Marriage date"
                  required
                  extra="The request cannot be made more than 30 days after this date."
                  {...errorProps("eventDate")}
                >
                  <DatePicker
                    value={eventDate ? dayjs(eventDate) : null}
                    onChange={(date) => setEventDate(date ? date.toDate() : undefined)}
                    format="YYYY-MM-DD"
                    className="w-full"
                  />
                </Form.Item>
              )}

              {leaveType === "MARRIAGE" && (
                <Form.Item
                  label={
                    isMarriageAfterWedding
                      ? "Marriage certificate"
                      : "Marriage certificate (optional now)"
                  }
                  required={isMarriageAfterWedding && !isEditMode}
                  extra={
                    !isMarriageAfterWedding
                      ? "Not required yet - must be uploaded within 15 days of the marriage date."
                      : "Required, since the request is made after the marriage date."
                  }
                  {...errorProps("documentFile")}
                >
                  <div style={errorBoxStyle("documentFile")}>
                    <Upload
                      beforeUpload={() => false}
                      fileList={documentFileList}
                      onChange={({ fileList: newFileList }) =>
                        setDocumentFileList(newFileList.slice(-1))
                      }
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Select file</Button>
                    </Upload>
                  </div>
                </Form.Item>
              )}
            </div>

            {!(leaveType === "DEATH_OF_RELATIVE" && specialCase) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  label="Start date"
                  extra={START_END_DATE_EXTRA[leaveType]}
                >
                  <DatePicker
                    value={dayjs(startDate)}
                    onChange={(date) => {
                      if (!date) return;
                      const newStart = date.toDate();
                      setStartDate(newStart);
                      if (leaveType === "CHILD_BIRTH") {
                        setEndDate(
                          computeChildBirthEndDate(
                            newStart,
                            puericulturaRequested,
                          ),
                        );
                      }
                    }}
                    format="YYYY-MM-DD"
                    className="w-full"
                  />
                </Form.Item>

                <Form.Item
                  label="End date"
                  extra={START_END_DATE_EXTRA[leaveType]}
                >
                  <DatePicker
                    value={dayjs(endDate)}
                    onChange={(date) => {
                      if (date) setEndDate(date.toDate());
                    }}
                    format="YYYY-MM-DD"
                    disabled={leaveType === "CHILD_BIRTH"}
                    disabledDate={(current) =>
                      !!current &&
                      (current.isBefore(dayjs(startDate), "day") ||
                        current.year() !== dayjs(startDate).year())
                    }
                    className="w-full"
                  />
                </Form.Item>
              </div>
            )}

            <Form.Item
              label={
                <span>
                  Substitute <em>(optional)</em>
                </span>
              }
              extra="The colleague who will cover for you while you're away."
            >
              <SearchInput
                onSelect={(u) => {
                  setSubstitute(u);
                  setSubstituteSearchValue(
                    u ? `${u.firstName} ${u.lastName}` : "",
                  );
                }}
                options={users}
                toDisplay={["firstName", "lastName"]}
                value={substituteSearchValue}
                onChangeValue={setSubstituteSearchValue}
              />
            </Form.Item>

            <Form.Item
              label="Comments"
              required={leaveType === "UNPAID_LEAVE"}
              extra={COMMENT_EXTRA[leaveType] ?? "Optional additional context for this request."}
              {...errorProps("comment")}
            >
              <Input.TextArea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              label={
                isEditMode ? (
                  <span>
                    Request signature <em>(optional - only if it changed)</em>
                  </span>
                ) : (
                  "Request signature"
                )
              }
              extra={
                isEditMode
                  ? "Only needed if you want to update your saved signature."
                  : "Required to submit the request - your signature will appear on the generated PDF."
              }
              {...errorProps("signature")}
            >
              <div style={errorBoxStyle("signature")}>
                {useAutoSignature ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={mySignature?.signatureImage ?? undefined}
                      alt="Your saved signature"
                      style={{ maxHeight: 60, maxWidth: 200 }}
                    />
                    <Typography.Text type="secondary">
                      Using your saved signature (My Signature)
                    </Typography.Text>
                    <CheckCircleTwoTone twoToneColor="#52c41a" />
                  </div>
                ) : (
                  <SignLeaveBtn open={isOpen} />
                )}
              </div>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default LeaveReqModal;
